import { WorkshopAvailabilityClient, WorkshopAvailabilityDto2 } from 'online-customer-services-ts'

import {
  Adviser,
  Availability,
  WorkshopAvailability,
  WorkshopAvailabilityRequest
} from '@/core/availability/domain/Availability.ts'
import { AvailabilityRepository } from '@/core/availability/domain/AvailabilityRepository.ts'
import { createWorkshopDate } from '@/utils'
import dayjs from '@/utils/dayjs.ts'

export class AvailabilityRepositoryBackendClient implements AvailabilityRepository {
  private readonly workshopAvailabilityClient: WorkshopAvailabilityClient

  constructor(url: string) {
    this.workshopAvailabilityClient = new WorkshopAvailabilityClient(url)
  }

  async getWorkshopAvailability(
    requestData: WorkshopAvailabilityRequest
  ): Promise<WorkshopAvailability> {
    const { date, hostId, services, servicesTotalUdts, teamId, vehicle, workshop } = requestData

    const fromDate = createWorkshopDate(
      dayjs(date.toISOString()).format('YYYY-MM-DD'),
      workshop.startingWorkHour!
    )
    const toDate = createWorkshopDate(
      dayjs(date.toISOString()).format('YYYY-MM-DD'),
      workshop.endingWorkHour!
    )

    const selectedDate = dayjs(date.toISOString()).isToday()
      ? dayjs(date.toISOString()).add(workshop.daysOfMarginToRequest!, 'day').toDate()
      : date

    const servicesIds = services.map((service) => Number(service.id))

    const response = await this.workshopAvailabilityClient.getWorkshopAvailability22(
      hostId.trim(),
      selectedDate,
      fromDate,
      toDate,
      workshop.id,
      servicesIds,
      vehicle.brandCode.trim(),
      vehicle.vin.trim(),
      servicesTotalUdts,
      teamId
    )

    const campaigns =
      response.campaigns?.map((campaign) => ({
        code: campaign.campaignCode ?? '',
        hours: campaign.necessaryHours ?? NaN,
        name: campaign.campaignName ?? ''
      })) || []

    const availability = transformAvailability(response.workshopAvailability ?? [])

    return cleanWorkshopAvailability({
      availability: availability,
      campaigns: campaigns
    })
  }
}

function cleanWorkshopAvailability(
  workshopAvailability: WorkshopAvailability
): WorkshopAvailability {
  const cleanedAvailability: Record<string, Availability> = {}

  for (const key in workshopAvailability.availability) {
    if (Object.prototype.hasOwnProperty.call(workshopAvailability.availability, key)) {
      const availabilityEntry = workshopAvailability.availability[key]
      if (availabilityEntry.advisers.length > 0) {
        cleanedAvailability[key] = availabilityEntry
      }
    }
  }

  return {
    ...workshopAvailability,
    availability: cleanedAvailability
  }
}

function transformAvailability(originalData: WorkshopAvailabilityDto2[]) {
  const availability: Record<string, Availability> = {}

  if (!originalData.length) {
    return {}
  }

  originalData.forEach((dayData) => {
    const date = dayData.monthDay?.toISOString().split('T')[0]
    const teamId = dayData.teamId ?? NaN

    const advisersMapForDate: Record<string, Adviser> = {}

    dayData.advisers?.forEach((adviserSlot) => {
      const adviserIdStr = adviserSlot.adviserId?.toString()
      const adviserName = adviserSlot.adviserName
      const adviserTime = adviserSlot.adviserTime

      if (!advisersMapForDate[adviserIdStr as string]) {
        advisersMapForDate[adviserIdStr as string] = {
          availableHours: [],
          id: adviserIdStr ?? '',
          name: adviserName ?? ''
        }
      }

      if (!advisersMapForDate[adviserIdStr as string].availableHours.includes(adviserTime!)) {
        advisersMapForDate[adviserIdStr as string].availableHours.push(adviserTime!)
      }
    })

    const advisersArrayForDate = Object.values(advisersMapForDate)

    advisersArrayForDate.forEach((adviser) => {
      adviser.availableHours.sort()
    })

    advisersArrayForDate.sort((a, b) => a.id.localeCompare(b.id))

    availability[date as string] = {
      advisers: advisersArrayForDate,
      teamId: teamId
    }
  })

  return availability
}
